import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Cancel, CheckCircle, Key, Close } from "@mui/icons-material";
import {
    IconButton,
    Button,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
} from "@mui/material";

import AppTable from "components/Shared/AppTable/AppTable";
import OpenInNewTab from "components/Shared/OpenInNewTab";
import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import { useTablePagination, useAuth } from "hooks";

import EBillProviderCredentialsTab from "./EBillProviderCredentialsTab";

import {
    useDeleteCredentialMutation,
    useGetEntityEBillProviderDDLQuery,
    useLazyListCredentialsQuery,
    useUseEBillProviderMutation,
} from "store/services/eBillProviderApi";

import { useGetEntityEBillProviderQuery } from "store/services/entityApi";


const EntityEBillProvider = () => {

    const { id } = useParams();

    const [selectedDDLProvider, setSelectedDDLProvider] = useState("");

    const [showDDL, setShowDDL] = useState(false);

    const [credentialsModalOpen, setCredentialsModalOpen] = useState(false);

    const [selectedProvider, setSelectedProvider] = useState(null);

    const [changeProviderDialogOpen, setChangeProviderDialogOpen] = useState(false);

    const [clearingCredentials, setClearingCredentials] = useState(false);

    const { isEntityAdmin, isAccountAdmin } = useAuth();

    const canManageProviders = isEntityAdmin || isAccountAdmin;

    const { page, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();
    const queryParams = { page, limit: perPage };

    const [fetchCredentials] = useLazyListCredentialsQuery();

    const [deleteCredential] = useDeleteCredentialMutation();

    const [triggerEBillProvider] = useUseEBillProviderMutation();

    const {
        data: entityEBillProviderData,
        isLoading,
        refetch,
    } = useGetEntityEBillProviderQuery({ id, ...queryParams });

    const tableData = entityEBillProviderData?.data || [];
    const hasProviders = tableData.length > 0;
    const currentProvider = tableData.find((row) => row?.isSelected) || tableData[0] || null;
    const currentProviderId = currentProvider?._id || currentProvider?.id || null;
    const currentProviderName = currentProvider?.name || "current provider";

    const { data: ddlData, refetch: refetchDDL } = useGetEntityEBillProviderDDLQuery(
        { entityId: id },
        { skip: !id }
    );
    const ddlProviders = ddlData?.data || [];
    const listedProviderIds = new Set(
        (tableData || []).map((provider) => String(provider?._id || provider?.id))
    );
    const ddlProvidersFiltered = ddlProviders.filter(
        (provider) => !listedProviderIds.has(String(provider?._id || provider?.id))
    );

    const clearCurrentProviderCredentials = async () => {
        if (!currentProviderId) return;
        const response = await fetchCredentials({
            id: currentProviderId,
            entityId: id,
        }).unwrap();

        const credentials = response?.data || [];
        const keys = Array.from(
            new Set(
                credentials
                    .map((cred) => String(cred?.key || "").trim())
                    .filter(Boolean)
            )
        );

        if (keys.length === 0) return;

        for (const key of keys) {
            await deleteCredential({ id: currentProviderId, key, entityId: id }).unwrap();
        }
    };

    const handleToggleProvider = async (row) => {
        const providerId = row?._id || row?.id;
        if (!providerId) {
            toast.error("Invalid provider id");
            return;
        }

        const isChangingProvider =
            !row?.isSelected &&
            currentProviderId &&
            String(currentProviderId) !== String(providerId);

        if (isChangingProvider) {
            const confirm = window.confirm(
                `Changing the provider will remove all credentials saved for ${currentProviderName}. Do you want to continue?`
            );
            if (!confirm) return;
            try {
                await clearCurrentProviderCredentials();
            } catch (error) {
                toast.error(error?.data?.message || "Failed to clear credentials for the current provider");
                return;
            }
        } else {
            const confirm = window.confirm(
                row?.isSelected
                    ? "Disable this provider for the entity?"
                    : "Enable this provider for the entity?"
            );
            if (!confirm) return;
        }

        try {
            await triggerEBillProvider({ providerId, entityId: id }).unwrap();
            toast.success(
                row?.isSelected
                    ? "Provider disabled successfully"
                    : "Provider enabled successfully"
            );
            refetch();
        } catch (error) {
            toast.error(error?.data?.message || "Failed to update provider usage");
        }
    };

    const handleSetupProvider = async () => {
        if (!selectedDDLProvider) {
            toast.warning("Please select a provider first");
            return;
        }
        try {
            await triggerEBillProvider({
                providerId: selectedDDLProvider,
                entityId: id,
            }).unwrap();
            toast.success("Provider enabled successfully");
            setSelectedDDLProvider("");
            setShowDDL(false);
            refetch();
            refetchDDL();
        } catch (error) {
            toast.error(error?.data?.message || "Failed to enable provider");
        }
    };

    const handleShowChangeProvider = () => setChangeProviderDialogOpen(true);

    const handleConfirmChangeProvider = async () => {
        setClearingCredentials(true);
        try {
            await clearCurrentProviderCredentials();
            toast.success("Credentials cleared successfully");
            setSelectedDDLProvider("");
            setShowDDL(true);
            setChangeProviderDialogOpen(false);
        } catch (error) {
            toast.error(
                error?.data?.message || "Failed to clear credentials for the current provider"
            );
        } finally {
            setClearingCredentials(false);
        }
    };

    const handleOpenCredentials = (row) => {
        setSelectedProvider(row);
        setCredentialsModalOpen(true);
    };

    const handleCloseCredentials = () => {
        setSelectedProvider(null);
        setCredentialsModalOpen(false);
    };

    const baseColumns = [
        {
            name: "Name",
            selector: (row) => row?.name,
            cell: (row) => (
                <OpenInNewTab
                    label={row?.name}
                    link={`/app/crm/ebill-provider/${row?._id}/detail`}
                />
            ),
        },
    ];

    const adminColumns = [
        {
            name: "Enable",
            selector: (row) => row?.isSelected,
            cell: (row) => (
                <IconButton size="small" onClick={() => handleToggleProvider(row)}>
                    {row?.isSelected ? (
                        <CheckCircle sx={{ color: "success.main" }} />
                    ) : (
                        <Cancel sx={{ color: "error.main" }} />
                    )}
                </IconButton>
            ),
            ignoreRowClick: true,
            right: true,
        },
        {
            name: "Credentials",
            selector: (row) => row?.isSelected,
            cell: (row) =>
                row?.isSelected ? (
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Key />}
                        onClick={() => handleOpenCredentials(row)}
                    >
                        Manage
                    </Button>
                ) : null,
            ignoreRowClick: true,
            right: true,
        },
    ];

    const columns = canManageProviders ? [...baseColumns, ...adminColumns] : baseColumns;

    // Render 

    return (
        <>
            {/* Setup Box */}
            {!hasProviders && !showDDL && (
                <Box
                    sx={{
                        mb: 3,
                        p: "28px 32px",
                        borderRadius: "10px",
                        backgroundColor: "background.paper",
                        border: (theme) =>
                            `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#c9e0ff"}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 3,
                    }}
                >
                    <Box>
                        <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "text.primary", mb: 0.4 }}>
                            No E-Bill provider configured
                        </Typography>
                        <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", lineHeight: 1.5 }}>
                            Connect a provider to start sending electronic bills for this entity.
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setShowDDL(true)}
                        disableElevation
                        sx={{ borderRadius: "8px", px: 3, py: 1 }}
                    >
                        Set up provider
                    </Button>
                </Box>
            )}

            {/* Change Provider Prompt */}
            {hasProviders && !showDDL && (
                <Box
                    sx={{
                        mb: 3,
                        p: "16px 20px",
                        borderRadius: "10px",
                        backgroundColor: "background.paper",
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                    }}
                >
                    <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                        Provider is already selected. Do you want to change the provider?
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={handleShowChangeProvider}
                        sx={{ borderRadius: "8px", px: 3 }}
                    >
                        Change Provider
                    </Button>
                </Box>
            )}

            {/* DDL Section */}
            {showDDL && (
                <Box
                    sx={{
                        mb: 3,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: "12px 16px",
                        borderRadius: "10px",
                        backgroundColor: "background.paper",
                        border: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <FormControl size="small" sx={{ minWidth: 260 }}>
                        <InputLabel>Select E-Bill Provider</InputLabel>
                        <Select
                            value={selectedDDLProvider}
                            label="Select E-Bill Provider"
                            onChange={(e) => setSelectedDDLProvider(e.target.value)}
                        >
                            {ddlProvidersFiltered.length === 0 ? (
                                <MenuItem disabled value="">
                                    No providers available
                                </MenuItem>
                            ) : (
                                ddlProvidersFiltered.map((provider) => (
                                    <MenuItem key={provider._id} value={provider._id}>
                                        {provider.name}
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        onClick={handleSetupProvider}
                        disabled={ddlProvidersFiltered.length === 0}
                    >
                        Use
                    </Button>
                </Box>
            )}

            {/* Table */}
            <AppTable
                columns={columns}
                data={tableData}
                progressPending={isLoading}
                paginationTotalRows={entityEBillProviderData?.totalItems || 0}
                paginationServer
                paginationPerPage={perPage}
                paginationDefaultPage={page}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
            />

            {/* Change Provider Warning Dialog */}
            <AppConfirmDialog
                open={changeProviderDialogOpen}
                onClose={() => setChangeProviderDialogOpen(false)}
                onConfirm={handleConfirmChangeProvider}
                title="Change E-Bill Provider?"
                confirmText="Change"
                loading={clearingCredentials}
                loadingText="Clearing credentials"
            >
                <Typography sx={{ fontSize: "0.9rem", color: "text.secondary" }}>
                    Changing the provider will remove all credentials saved for{" "}
                    <strong>{currentProviderName}</strong> for this entity. This action cannot be undone.
                </Typography>
            </AppConfirmDialog>

            {/* Credentials Dialog */}
            <Dialog
                open={credentialsModalOpen}
                onClose={handleCloseCredentials}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
                    Manage Credentials - {selectedProvider?.name}
                    <IconButton onClick={handleCloseCredentials} size="small">
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedProvider && (
                        <EBillProviderCredentialsTab
                            providerId={selectedProvider._id}
                            entityId={id}
                            mode="entity"
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default EntityEBillProvider;